import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy } from "@ngneat/until-destroy";
import {
  BottomNavigationPanel,
  FormConfiguration,
  FormFactory,
  FormRow,
  FunnelHeader,
  Section,
} from "pax-ui/components";
import { combineLatest, map, Observable } from "rxjs";
import { CheckoutFormWrapperComponent } from "src/app/quote/shared/components/checkout-form-wrapper/checkout-form-wrapper.component";
import { FormName } from "src/app/shared/models/form-name";
import { ProductSpecificCustomRouteData } from "src/app/shared/routes/shared/shared-route.constant";
import { DataAnalyticsEventPublisherService } from "src/app/shared/services/data-analytics/data-analytics-event-publisher.service";
import { ExternalSystemCommunicationStateService } from "src/app/shared/services/external-system-communication-state.service";
import { FormComponentService } from "src/app/shared/services/form/form-component.service";
import { FormService } from "src/app/shared/services/form/form.service";
import { RouteDataService } from "src/app/shared/services/route-data.service";
import { RouterService } from "src/app/shared/services/router.service";
import {
  QuoteStoreFacade,
  SubmitParameters,
  UnderwritingQuestionPageState,
} from "src/app/shared/store/quote/quote-store.facade";
import {
  UnderwritingQuestion,
  UnderwritingQuestionAnswer,
} from "src/app/shared/store/quote/shared/shared.state";

@UntilDestroy(this)
@Component({
  selector: "app-underwriting-questions",
  imports: [
    CommonModule,
    Section,
    FunnelHeader,
    BottomNavigationPanel,
    CheckoutFormWrapperComponent,
    FormFactory,
  ],
  templateUrl: "./underwriting-questions.page.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnderwritingQuestionsPage implements OnInit {
  readonly formName = FormName.UNDERWRITING_QUESTIONS;
  readonly notEnoughInformationToDisplayUnderwritingQuestionsText = $localize`Leider haben wir noch nicht genug Informationen, um die Gesundheitsfragen darzustellen.`;
  readonly emoji = "🩺";

  formConfiguration$!: Observable<FormConfiguration>;
  underwritingQuestionsData: UnderwritingQuestionsFormModel | undefined;
  productSpecificCustomRouteData: ProductSpecificCustomRouteData;

  hasNoUnderwritingQuestions$: Observable<boolean>;

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _formService: FormService,
    private readonly _routeDataService: RouteDataService,
    private readonly _routerService: RouterService,
    private readonly _formComponentService: FormComponentService,
    private readonly _quoteStoreFacade: QuoteStoreFacade,
    public readonly externalSystemCommunicationStateService: ExternalSystemCommunicationStateService,
    private readonly _dataAnalyticsEventPublisherService: DataAnalyticsEventPublisherService,
  ) {
    this.productSpecificCustomRouteData =
      this._routeDataService.getProductSpecificCustomRouteData(
        this._activatedRoute,
      );

    this._dataAnalyticsEventPublisherService.pushFunnelViewEvent(
      this.productSpecificCustomRouteData.product,
      this.productSpecificCustomRouteData.page,
      this.productSpecificCustomRouteData.dataAnalytics.funnelStepNumber,
    );

    this.hasNoUnderwritingQuestions$ =
      this.getHasNoUnderwritingQuestionsObservable();
  }

  ngOnInit() {
    this.formConfiguration$ = this.getFormConfigurationObservable();
  }

  processFormSubmit(
    underwritingQuestionsFormModel: UnderwritingQuestionsFormModel,
  ) {
    const submitParameters: SubmitParameters = {
      originPage: this.productSpecificCustomRouteData.page,
      nextNavigationPage:
        this.productSpecificCustomRouteData.navigation.nextNavigationPage,
      formModelContainer: {
        underwritingQuestionsForm: underwritingQuestionsFormModel,
      },
    };

    this._quoteStoreFacade.dispatchSubmitQuote(
      this.productSpecificCustomRouteData.product,
      submitParameters,
    );
  }

  submitForm() {
    this._formService.submitForm(this.formName);
  }

  async tryNavigateBack() {
    await this._routerService.tryNavigateToPage(
      this.productSpecificCustomRouteData.navigation.previousNavigationPage,
    );
  }

  private getHasNoUnderwritingQuestionsObservable() {
    return combineLatest([
      this._quoteStoreFacade.getUnderwritingQuestionPageState(
        this.productSpecificCustomRouteData.product,
      ),
      this.externalSystemCommunicationStateService
        .hasAnyPendingBackendRequests$,
    ]).pipe(
      map(([underwritingQuestionsState, hasAnyPendingBackendRequests]) => {
        const hasNoUnderwritingQuestions = this.checkHasNoUnderwritingQuestions(
          underwritingQuestionsState,
          hasAnyPendingBackendRequests,
        );

        return hasNoUnderwritingQuestions;
      }),
    );
  }

  private getFormConfigurationObservable() {
    return this._quoteStoreFacade
      .getUnderwritingQuestionPageState(
        this.productSpecificCustomRouteData.product,
      )
      .pipe(
        map((underwritingQuestionsState) => {
          return this.createFormConfiguration(
            underwritingQuestionsState.hasUnderwritingQuestionsPassed,
            underwritingQuestionsState.underwritingQuestions,
            underwritingQuestionsState.underwritingQuestionAnswers,
          );
        }),
      );
  }

  private checkHasNoUnderwritingQuestions(
    underwritingQuestionPageState: UnderwritingQuestionPageState,
    hasAnyPendingBackendRequests: boolean,
  ) {
    const hasNoPendingBackendRequests = !hasAnyPendingBackendRequests;

    const hasNoUnderwritingQuestions =
      underwritingQuestionPageState.underwritingQuestions === undefined;

    return hasNoPendingBackendRequests && hasNoUnderwritingQuestions;
  }

  private createFormConfiguration(
    hasUnderwritingQuestionsPassed?: boolean | null,
    underwritingQuestions?: UnderwritingQuestion[],
    underwritingQuestionAnswers?: UnderwritingQuestionAnswer[] | null,
  ) {
    const defaultFormConfiguration: FormConfiguration = {
      name: this.formName,
      rows: [
        {
          items: [],
        },
      ],
    };

    const underwritingQuestionsFormModel: UnderwritingQuestionsFormModel = {};

    const formRows = underwritingQuestions?.map((underwritingQuestion) => {
      const hasUserAlreadySaidYes = this.tryGetAnswer(
        underwritingQuestion,
        hasUnderwritingQuestionsPassed,
        underwritingQuestionAnswers,
      );

      if (hasUserAlreadySaidYes !== undefined) {
        underwritingQuestionsFormModel[underwritingQuestion.id] =
          hasUserAlreadySaidYes;
      }

      return this.createSingleQuestionFormRow(underwritingQuestion);
    });

    if (formRows) {
      defaultFormConfiguration.rows = formRows;
    }

    this.underwritingQuestionsData = underwritingQuestionsFormModel;

    return defaultFormConfiguration;
  }

  private createSingleQuestionFormRow(
    underwritingQuestion: UnderwritingQuestion,
  ) {
    const underwritingQuestionField =
      this._formComponentService.getUnderwritingQuestionField(
        underwritingQuestion,
      );

    const formRow: FormRow = {
      items: [underwritingQuestionField],
    };

    return formRow;
  }

  private tryGetAnswer(
    underwritingQuestion: UnderwritingQuestion,
    hasUnderwritingQuestionsPassed?: boolean | null,
    underwritingQuestionAnswers?: UnderwritingQuestionAnswer[] | null,
  ) {
    // We do not store any answers and thus we can only preselect answers in case
    // we have 'hasUnderwritingQuestionsPassed' stored with 'true' in the backend.
    if (hasUnderwritingQuestionsPassed) {
      return false;
    }

    return underwritingQuestionAnswers?.find(
      (answeredQuestion) =>
        underwritingQuestion.id === answeredQuestion.questionId,
    )?.saidYes;
  }
}

export type UnderwritingQuestionsFormModel = Record<string, boolean>;
